import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, apiBaseUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Download, FileText, BarChart3, Users, ListTree,
  AlertTriangle, Loader2, RefreshCw, Filter,
} from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PERIODS = [
  { key: "weekly", label: "Weekly (Last 7d)" },
  { key: "monthly", label: "Monthly (Last 30d)" },
  { key: "custom", label: "Custom Range" },
];

const TABS = [
  { key: "summary",     label: "Summary",     icon: BarChart3 },
  { key: "by_designer", label: "By Designer", icon: Users },
  { key: "projects",    label: "Projects",    icon: ListTree },
  { key: "escalations", label: "Escalations", icon: AlertTriangle },
];

export default function DesignReportsPage() {
  const { principal } = useAuth();
  const [tab, setTab] = useState("summary");
  const [period, setPeriod] = useState("monthly");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [designManagerId, setDesignManagerId] = useState("");
  const [designerId, setDesignerId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [city, setCity] = useState("");
  const [filters, setFilters] = useState({ cities: [], statuses: [], designers: [], design_managers: [], team_scoped: false });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load filter dropdowns once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/design-reports/filters");
        setFilters(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    })();
  }, []);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams({ period });
    if (period === "custom") {
      if (from) p.append("from", new Date(from).toISOString());
      if (to) p.append("to", new Date(to + "T23:59:59").toISOString());
    }
    if (designManagerId) p.append("design_manager_id", designManagerId);
    if (designerId) p.append("designer_id", designerId);
    if (statusFilter) p.append("status", statusFilter);
    if (city) p.append("city", city);
    return p;
  }, [period, from, to, designManagerId, designerId, statusFilter, city]);

  const generate = useCallback(async () => {
    if (period === "custom" && (!from || !to)) {
      toast.error("Pick a From and To date for a custom range");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/design-reports?${queryParams.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate report");
    } finally { setLoading(false); }
  }, [queryParams, period, from, to]);

  const downloadCsv = async () => {
    try {
      const resp = await api.get(`/design-reports.csv?${queryParams.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `design-report-${period}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV download failed");
    }
  };

  const downloadPdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const marginL = 40;
    let y = 50;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Interazzo · Design Performance Report", marginL, y);
    y += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(
      `Window: ${new Date(data.window.from).toLocaleDateString()} → ${new Date(data.window.to).toLocaleDateString()}`,
      marginL, y);
    y += 20;

    doc.setTextColor(0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Summary", marginL, y);
    y += 12;
    autoTable(doc, {
      startY: y,
      head: [["Metric", "Value"]],
      body: [
        ["Total Projects", data.summary.total_projects],
        ["On-time Delivery Rate (%)", `${data.summary.on_time_rate}`],
        ["Delayed Projects", data.summary.delayed_count],
        ["Avg SLA Adherence / Designer (%)", `${data.summary.avg_sla_adherence_per_designer}`],
        ["Portfolio Value", `₹ ${data.summary.portfolio_value.toLocaleString("en-IN")}`],
        ["Pending Overage Billing", data.summary.pending_overage_billing_count],
      ],
      theme: "grid",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [212, 175, 55], textColor: 0 },
    });
    y = doc.lastAutoTable.finalY + 20;

    if (data.by_designer.length) {
      doc.setFont("helvetica", "bold");
      doc.text("By Designer", marginL, y);
      y += 6;
      autoTable(doc, {
        startY: y,
        head: [["Designer", "Projects", "On-time", "Delayed", "On-time %", "Avg Delay (d)", "3D Rev", "2D Rev", "Overage", "Leave"]],
        body: data.by_designer.map((a) => [
          a.designer_name, a.projects, a.on_time, a.delayed,
          a.on_time_rate, a.avg_delay_days,
          a.revisions_3d, a.revisions_2d ?? 0, a.overage_flags, a.leave_days,
        ]),
        theme: "striped",
        styles: { fontSize: 8 },
        headStyles: { fillColor: [212, 175, 55], textColor: 0 },
      });
      y = doc.lastAutoTable.finalY + 20;
    }

    if (data.projects.length) {
      doc.addPage();
      y = 50;
      doc.setFont("helvetica", "bold");
      doc.text("Project Detail", marginL, y);
      y += 6;
      autoTable(doc, {
        startY: y,
        head: [["Client", "Designer", "Manager", "Amount", "Status", "3D", "2D", "Delay(d)", "Alerts"]],
        body: data.projects.map((r) => [
          r.client_name || "—",
          r.designer_name,
          r.design_manager_name,
          `₹ ${(r.amount || 0).toLocaleString("en-IN")}`,
          r.status,
          r.sets_3d !== null ? `${r.sets_3d}/${r.sets_3d_target}` : `—/${r.sets_3d_target}`,
          r.sets_2d !== null ? `${r.sets_2d}/${r.sets_2d_target}` : `—/${r.sets_2d_target}`,
          r.delay_days || "",
          (r.alerts || []).join(", "),
        ]),
        theme: "striped",
        styles: { fontSize: 7 },
        headStyles: { fillColor: [212, 175, 55], textColor: 0 },
      });
    }

    doc.save(`design-report-${data.window.period}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF downloaded");
  };

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-6 md:px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design · Analytics</p>
            <h1 className="font-display text-2xl md:text-3xl text-white mt-1">Performance Reports</h1>
            {filters.team_scoped && (
              <p className="text-[11px] text-gray-500 mt-1 italic">Scope: your team only.</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={generate} disabled={loading}
                    className="iz-btn-primary flex items-center gap-2"
                    data-testid="design-report-generate">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
              Generate Report
            </button>
            <button onClick={downloadCsv} disabled={!data}
                    className="iz-btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="design-report-csv">
              <Download className="w-3.5 h-3.5"/> CSV
            </button>
            <button onClick={downloadPdf} disabled={!data}
                    className="iz-btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-40"
                    data-testid="design-report-pdf">
              <FileText className="w-3.5 h-3.5"/> PDF
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-10 py-6 space-y-5">
        {/* Filters bar */}
        <div className="iz-card p-4 space-y-3" data-testid="design-report-filters">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
            <Filter className="w-3.5 h-3.5"/> Filters
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Period">
              <select value={period} onChange={(e) => setPeriod(e.target.value)}
                      className="iz-input w-full text-xs"
                      data-testid="filter-period">
                {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </Field>
            {period === "custom" && (
              <>
                <Field label="From">
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                         className="iz-input w-full text-xs" data-testid="filter-from"/>
                </Field>
                <Field label="To">
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                         className="iz-input w-full text-xs" data-testid="filter-to"/>
                </Field>
              </>
            )}
            <Field label="Design Manager">
              <select value={designManagerId} onChange={(e) => setDesignManagerId(e.target.value)}
                      className="iz-input w-full text-xs" data-testid="filter-manager">
                <option value="">All</option>
                {filters.design_managers.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Designer">
              <select value={designerId} onChange={(e) => setDesignerId(e.target.value)}
                      className="iz-input w-full text-xs" data-testid="filter-designer">
                <option value="">All</option>
                {filters.designers.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                      className="iz-input w-full text-xs" data-testid="filter-status">
                <option value="">All</option>
                {filters.statuses.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </select>
            </Field>
            <Field label="City">
              <select value={city} onChange={(e) => setCity(e.target.value)}
                      className="iz-input w-full text-xs" data-testid="filter-city">
                <option value="">All</option>
                {filters.cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>
        </div>

        {/* Results */}
        {!data ? (
          <div className="iz-card p-10 text-center text-sm text-gray-500 italic"
               data-testid="design-report-empty">
            Set your filters and click <span className="text-[#d4af37]">Generate Report</span> to view analytics.
          </div>
        ) : (
          <>
            <div className="iz-card p-2 flex flex-wrap gap-1" data-testid="design-report-tabs">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)}
                        className={`px-4 py-2 rounded-md text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${
                          tab === t.key
                            ? "bg-[#d4af37] text-black"
                            : "text-gray-400 hover:text-white hover:bg-[#111]"
                        }`}
                        data-testid={`dr-tab-${t.key}`}>
                  <t.icon className="w-3.5 h-3.5"/> {t.label}
                </button>
              ))}
            </div>

            {tab === "summary" && <SummaryPanel data={data}/>}
            {tab === "by_designer" && <ByDesignerTable rows={data.by_designer}/>}
            {tab === "projects" && <ProjectDetailTable rows={data.projects}/>}
            {tab === "escalations" && <EscalationsPanel data={data}/>}
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function StatCard({ label, value, testid, tone }) {
  return (
    <div className="iz-card p-4" data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</p>
      <p className={`text-2xl font-display mt-1 ${tone || "text-white"}`}>{value}</p>
    </div>
  );
}

function SummaryPanel({ data }) {
  const s = data.summary;
  const w = data.window;
  return (
    <div className="space-y-3">
      <p className="text-[11px] text-gray-500">
        Window: <span className="text-white">{new Date(w.from).toLocaleDateString()}</span> →{" "}
        <span className="text-white">{new Date(w.to).toLocaleDateString()}</span>
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="design-summary-cards">
        <StatCard label="Total Projects" value={s.total_projects} testid="stat-total"/>
        <StatCard label="On-time Rate" value={`${s.on_time_rate}%`} testid="stat-ontime"
                  tone={s.on_time_rate >= 80 ? "text-emerald-400" : "text-[#d4af37]"}/>
        <StatCard label="Delayed" value={s.delayed_count} testid="stat-delayed"
                  tone={s.delayed_count === 0 ? "text-emerald-400" : "text-red-400"}/>
        <StatCard label="Avg SLA Adherence / Designer" value={`${s.avg_sla_adherence_per_designer}%`}
                  testid="stat-avg-sla"/>
        <StatCard label="Portfolio Value" value={`₹ ${(s.portfolio_value || 0).toLocaleString("en-IN")}`}
                  testid="stat-portfolio"/>
        <StatCard label="Pending Overage Billing" value={s.pending_overage_billing_count}
                  testid="stat-overage"/>
      </div>
    </div>
  );
}

function ByDesignerTable({ rows }) {
  if (!rows.length) return <EmptyRow msg="No designer activity in this window."/>;
  return (
    <div className="iz-card p-4 overflow-x-auto" data-testid="by-designer-table">
      <table className="w-full text-xs min-w-[900px]">
        <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
          <tr>
            <Th>Designer</Th><Th>Projects</Th><Th>On-time</Th><Th>Delayed</Th>
            <Th>On-time %</Th><Th>Avg Delay (d)</Th><Th>3D Rev</Th><Th>2D Rev</Th>
            <Th>Overage</Th><Th>Leave (d)</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.designer_id} className="border-b border-[#161616]"
                data-testid={`designer-row-${a.designer_id}`}>
              <Td>{a.designer_name}</Td>
              <Td>{a.projects}</Td>
              <Td className="text-emerald-400">{a.on_time}</Td>
              <Td className={a.delayed ? "text-red-400" : ""}>{a.delayed}</Td>
              <Td>{a.on_time_rate}%</Td>
              <Td>{a.avg_delay_days}</Td>
              <Td>{a.revisions_3d}</Td>
              <Td>{a.revisions_2d ?? "—"}</Td>
              <Td>{a.overage_flags}</Td>
              <Td>{a.leave_days}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectDetailTable({ rows }) {
  if (!rows.length) return <EmptyRow msg="No projects match the current filters."/>;
  return (
    <div className="iz-card p-4 overflow-x-auto" data-testid="project-detail-table">
      <table className="w-full text-xs min-w-[1100px]">
        <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
          <tr>
            <Th>Client</Th><Th>Designer</Th><Th>Manager</Th><Th>Amount</Th>
            <Th>Status</Th><Th>3D Sets</Th><Th>2D Sets</Th>
            <Th>Next Target Date</Th><Th>Submitted</Th><Th>Delay (d)</Th><Th>Alerts</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-[#161616]"
                data-testid={`project-row-${r.id}`}>
              <Td>{r.client_name || "—"}</Td>
              <Td>{r.designer_name}</Td>
              <Td>{r.design_manager_name}</Td>
              <Td>₹ {(r.amount || 0).toLocaleString("en-IN")}</Td>
              <Td className="text-gray-300">{(r.status || "").replace(/_/g, " ")}</Td>
              <Td>{r.sets_3d !== null ? `${r.sets_3d}/${r.sets_3d_target}` : `—/${r.sets_3d_target}`}</Td>
              <Td>{r.sets_2d !== null ? `${r.sets_2d}/${r.sets_2d_target}` : `—/${r.sets_2d_target}`}</Td>
              <Td>{r.sla_due_date ? new Date(r.sla_due_date).toLocaleDateString() : "—"}</Td>
              <Td>{r.submission_date ? new Date(r.submission_date).toLocaleDateString() : "—"}</Td>
              <Td className={r.delay_days > 0 ? "text-red-400" : ""}>{r.delay_days || "—"}</Td>
              <Td>
                {(r.alerts || []).map((a) => (
                  <span key={a} className="text-[9px] font-mono uppercase tracking-widest text-red-400 border border-red-500/40 rounded px-1 py-0.5 mr-1">
                    {a.replace(/_/g, " ")}
                  </span>
                ))}
                {(r.alerts || []).length === 0 && <span className="text-gray-600">—</span>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EscalationsPanel({ data }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div className="iz-card p-4" data-testid="escalations-critical">
        <p className="text-[10px] font-mono uppercase tracking-widest text-red-400 mb-2">
          Critical Escalations · 48h+ delay
        </p>
        {data.escalations.length === 0 ? (
          <p className="text-xs text-gray-500 italic">None in this window.</p>
        ) : (
          <ul className="space-y-2">
            {data.escalations.map((e) => (
              <li key={e.project_id} className="border border-red-500/30 rounded p-2">
                <p className="text-sm text-white">{e.client_name}</p>
                <p className="text-[10px] text-gray-500">
                  Designer: {e.designer_name} · Manager: {e.design_manager_name} ·{" "}
                  <span className="text-red-400">{e.delay_days} working days over</span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="iz-card p-4" data-testid="escalations-policy">
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mb-2">
          Policy Violations Logged
        </p>
        {(data.policy_violations || []).length === 0 ? (
          <p className="text-xs text-gray-500 italic">No policy-violation tracker wired yet.</p>
        ) : (
          <ul className="text-xs text-gray-300 space-y-1">
            {data.policy_violations.map((v, i) => <li key={i}>{JSON.stringify(v)}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left py-2 px-2">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`py-2 px-2 text-white ${className}`}>{children}</td>;
}
function EmptyRow({ msg }) {
  return <div className="iz-card p-10 text-center text-sm text-gray-500 italic">{msg}</div>;
}
